import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Eye, Factory, Printer } from "@phosphor-icons/react";

export default function Boxes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [, setSettings] = useState({});
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      const [b, s] = await Promise.all([api.get("/boxes"), api.get("/settings")]);
      setItems(b.data); setSettings(s.data);
    })();
  }, []);

  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((b) => b.id)));
  };

  // Preserve table order when printing.
  const orderedSelectedIds = useMemo(
    () => items.filter((b) => selected.has(b.id)).map((b) => b.id),
    [items, selected]
  );

  const printSelected = () => {
    if (orderedSelectedIds.length === 0) return;
    navigate(`/boxes/labels?ids=${orderedSelectedIds.join(",")}`);
  };

  return (
    <div className="space-y-6" data-testid="boxes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Boxes</h1>
          <p className="text-xs text-zinc-600 mt-1">
            Shipping boxes are auto-created from <Link to="/assembly-runs" className="underline font-bold">Assembly Runs</Link> for shippable parts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={printSelected}
            disabled={selected.size === 0}
            className="brut-btn brut-btn-dark px-4 py-2 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="print-selected-btn"
          >
            <Printer size={14} weight="bold" />
            Print {selected.size > 0 ? `${selected.size} ` : ""}Label{selected.size === 1 ? "" : "s"}
          </button>
          <Link to="/assembly-runs" className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="goto-assembly-runs">
            <Factory size={14} weight="bold" /> Start Assembly Run
          </Link>
        </div>
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="boxes-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  data-testid="select-all-boxes"
                  aria-label="Select all boxes"
                />
              </th>
              <th>Box #</th>
              <th>Product</th>
              <th>Units</th>
              <th>Project</th>
              <th>Assembly Run</th>
              <th>Assembled</th>
              <th>Packing List</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-zinc-500">No boxes yet. Start an Assembly Run.</td></tr>}
            {items.map((b) => (
              <tr key={b.id} data-testid={`box-row-${b.box_number}`} className={selected.has(b.id) ? "bg-amber-50" : ""}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(b.id)}
                    onChange={() => toggleOne(b.id)}
                    data-testid={`select-box-${b.box_number}`}
                    aria-label={`Select box ${b.box_number}`}
                  />
                </td>
                <td className="font-bold">
                  {b.box_number}
                  {b.is_partial && <span className="ml-1 brut-tag brut-tag-yellow" title="Partial box">P</span>}
                </td>
                <td>
                  <div className="font-mono-ui text-xs">{b.product_part_number || "—"}</div>
                  <div className="text-xs text-zinc-600">{b.product_name}</div>
                </td>
                <td className="font-mono-ui">{b.units_count}</td>
                <td className="text-xs">{b.target_project || "—"}</td>
                <td>
                  {b.assembly_run_number ? (
                    <Link to={`/assembly-runs/${b.assembly_run_id}`} className="brut-tag brut-tag-yellow underline font-mono-ui">{b.assembly_run_number}</Link>
                  ) : <span className="text-zinc-500">—</span>}
                </td>
                <td className="font-mono-ui">{new Date(b.created_at).toLocaleDateString()}</td>
                <td>
                  {b.packing_list_number ? (
                    <span className="brut-tag brut-tag-green">{b.packing_list_number}</span>
                  ) : (
                    <span className="brut-tag brut-tag-white">Pending</span>
                  )}
                </td>
                <td className="text-right whitespace-nowrap">
                  <Link to={`/boxes/${b.id}`} className="brut-tag brut-tag-yellow mr-1" data-testid={`view-box-${b.box_number}`}>
                    <Eye size={10} weight="bold" />
                  </Link>
                  <Link to={`/boxes/${b.id}/label`} className="brut-tag brut-tag-dark" data-testid={`label-box-${b.box_number}`}>
                    Label
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
